import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  User, 
  GraduationCap, 
  Edit2,
  Save,
  X,
  FileText,
  Award,
  Bookmark,
  Star,
  Clock,
  Download
} from 'lucide-react';
import { useProfile } from '../contexts/ProfileContext';
import Button from '../components/ui/Button';
import { jsPDF } from 'jspdf';

const Profile: React.FC = () => {
  const { profile, updateProfile, loading: profileLoading } = useProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    education_level: profile?.education_level || '',
    subjects: profile?.subjects || [],
    study_preference: profile?.study_preference || '',
    preferred_industries: profile?.preferred_industries || []
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    const value = e.target.value.split(',').map(item => item.trim());
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateProfile(formData);
    setIsEditing(false);
  };

  // Mock data for career reports
  const careerReports = [
    {
      id: 1,
      type: 'Questionnaire',
      title: 'Career Path Analysis',
      date: '2025-02-15',
      score: 85
    },
    {
      id: 2,
      type: 'Simulator',
      title: 'Software Developer Simulation',
      date: '2025-02-10',
      score: 92
    }
  ];

  // Mock data for saved scholarships
  const savedScholarships = [
    {
      id: 1,
      name: 'Tech Innovation Scholarship',
      provider: 'Google',
      deadline: '2025-05-01',
      amount: 10000
    },
    {
      id: 2,
      name: 'Women in STEM Grant',
      provider: 'Microsoft',
      deadline: '2025-06-15',
      amount: 15000
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  const handleExportReport = (reportId: number) => {
    const doc = new jsPDF();
    const report = careerReports.find(r => r.id === reportId);
    
    if (!report) return;

    doc.setFontSize(20);
    doc.text(report.title, 20, 20);
    
    doc.setFontSize(12);
    doc.text(`Date: ${report.date}`, 20, 40);
    doc.text(`Score: ${report.score}%`, 20, 50);
    
    doc.save(`career-report-${reportId}.pdf`);
  };

  return (
    <main className="container mx-auto px-4 py-8">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-4xl mx-auto space-y-8"
      >
        {/* Header */}
        <motion.div 
          variants={itemVariants}
          className="flex justify-between items-center"
        >
          <h1 className="text-3xl font-bold text-gray-900">Your Profile</h1>
          {!isEditing && (
            <Button
              variant="outline"
              onClick={() => setIsEditing(true)}
              icon={<Edit2 className="h-4 w-4" />}
            >
              Edit Profile
            </Button>
          )}
        </motion.div>

        {/* Personal Information Section */}
        <motion.div
          variants={itemVariants}
          className="bg-white shadow rounded-lg overflow-hidden"
        >
          {isEditing ? (
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  className="input"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Education Level
                </label>
                <select
                  name="education_level"
                  value={formData.education_level}
                  onChange={handleInputChange}
                  className="select"
                >
                  <option value="">Select education level</option>
                  <option value="high_school">High School</option>
                  <option value="bachelors">Bachelor's Degree</option>
                  <option value="masters">Master's Degree</option>
                  <option value="phd">PhD</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subjects (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.subjects.join(', ')}
                  onChange={(e) => handleArrayInputChange(e, 'subjects')}
                  className="input"
                  placeholder="Math, Physics, Computer Science"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Study Preference
                </label>
                <select
                  name="study_preference"
                  value={formData.study_preference}
                  onChange={handleInputChange}
                  className="select"
                >
                  <option value="">Select preference</option>
                  <option value="local">Local</option>
                  <option value="global">International</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Industries (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.preferred_industries.join(', ')}
                  onChange={(e) => handleArrayInputChange(e, 'preferred_industries')}
                  className="input"
                  placeholder="Technology, Healthcare, Finance"
                />
              </div>

              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                  icon={<X className="h-4 w-4" />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  isLoading={profileLoading}
                  icon={<Save className="h-4 w-4" />}
                >
                  Save Changes
                </Button>
              </div>
            </form>
          ) : (
            <div className="divide-y divide-gray-200">
              <ProfileSection
                icon={<User className="h-5 w-5 text-primary-500" />}
                title="Personal Information"
                content={
                  <div className="space-y-2">
                    <p><span className="font-medium">Name:</span> {profile?.full_name || 'Not set'}</p>
                    <p><span className="font-medium">Study Preference:</span> {profile?.study_preference || 'Not specified'}</p>
                  </div>
                }
              />

              <ProfileSection
                icon={<GraduationCap className="h-5 w-5 text-primary-500" />}
                title="Education"
                content={
                  <div className="space-y-2">
                    <p><span className="font-medium">Level:</span> {profile?.education_level || 'Not specified'}</p>
                    <div>
                      <span className="font-medium">Subjects:</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {profile?.subjects?.map((subject, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm"
                          >
                            {subject}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                }
              />

              <ProfileSection
                icon={<Award className="h-5 w-5 text-primary-500" />}
                title="Preferred Industries"
                content={
                  <div className="flex flex-wrap gap-2">
                    {profile?.preferred_industries?.map((industry, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm"
                      >
                        {industry}
                      </span>
                    ))}
                  </div>
                }
              />
            </div>
          )}
        </motion.div>

        {/* My Career Section */}
        <motion.div variants={itemVariants}>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">My Career</h2>
          
          {/* Career Reports */}
          <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 text-primary-500 mr-2" />
                Career Reports
              </h3>
              <div className="space-y-4">
                {careerReports.map((report) => (
                  <div key={report.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">{report.title}</h4>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        <span className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {report.date}
                        </span>
                        <span className="flex items-center">
                          <Star className="h-4 w-4 mr-1" />
                          Score: {report.score}%
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExportReport(report.id)}
                      icon={<Download className="h-4 w-4" />}
                    >
                      Export
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Saved Scholarships */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Bookmark className="h-5 w-5 text-primary-500 mr-2" />
                Saved Scholarships
              </h3>
              <div className="space-y-4">
                {savedScholarships.map((scholarship) => (
                  <div key={scholarship.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">{scholarship.name}</h4>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        <span>{scholarship.provider}</span>
                        <span>Deadline: {scholarship.deadline}</span>
                        <span>${scholarship.amount.toLocaleString()}</span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                    >
                      View Details
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </main>
  );
};

interface ProfileSectionProps {
  icon: React.ReactNode;
  title: string;
  content: React.ReactNode;
}

const ProfileSection: React.FC<ProfileSectionProps> = ({ icon, title, content }) => (
  <div className="p-6">
    <div className="flex items-center mb-4">
      {icon}
      <h2 className="text-lg font-semibold text-gray-900 ml-2">{title}</h2>
    </div>
    <div className="ml-7">
      {content}
    </div>
  </div>
);

export default Profile;